import { endOfDay, format, isValid, parse, startOfDay, startOfMonth, subDays } from 'date-fns';
import * as XLSX from 'xlsx-js-style';
import {
  FetchLedgerJournalsParams,
  JournalBookKind,
  JournalDatePreset,
  JournalFilterForm,
  JournalSegment,
  JournalSortOrder,
  JournalZohoStatus,
  LedgerJournal,
} from '../../types/ledgerJournal';
import {
  DATE_PRESETS,
  DEFAULT_PER_PAGE,
  DEFAULT_PRESET,
  EXPORT_HEADERS,
  JOURNAL_START_MARKER,
  PER_PAGE_OPTIONS,
  URL_DATE_FORMAT,
  URL_KEYS,
} from './journalExplorer.constants';

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

/** Everything the page reads from the URL. */
export interface JournalUrlState {
  form: JournalFilterForm;
  page: number;
  perPage: number;
  sortOrder: JournalSortOrder;
  book: JournalBookKind;
}

const sarFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatSar = (amount: number) => `SAR ${sarFormatter.format(amount || 0)}`;

export const formatAmount = (amount: number) => (amount ? sarFormatter.format(amount) : '');

export const formatDateTime = (iso?: string | null) => (iso ? format(new Date(iso), 'dd MMM yyyy, HH:mm:ss') : '-');

export const formatDate = (iso?: string | null) => (iso ? format(new Date(iso), 'dd MMM yyyy') : '-');

/** The searched range as one line — what the user picked, not where the journals fell. */
export const formatRange = (from: Date | null, to: Date | null) => {
  if (from && to) return `${format(from, 'dd MMM yyyy')} → ${format(to, 'dd MMM yyyy')}`;
  if (from) return `From ${format(from, 'dd MMM yyyy')}`;
  if (to) return `Until ${format(to, 'dd MMM yyyy')}`;

  return 'All time';
};

/** `FUND_MANAGER_SELL_UNIT` → `Fund Manager Sell Unit`. */
export const humanizeEvent = (event: string) => {
  if (!event) return 'Unknown';

  return event
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/** A colour per kind of movement, so the list scans by type. */
export const eventColor = (event: string): ChipColor => {
  if (event.includes('LOSS') || event.includes('REJECT') || event.includes('CANCEL')) return 'error';
  if (event.includes('GAIN')) return 'success';
  if (event.includes('FEE') || event.includes('FLUCTUATION') || event.includes('FX')) return 'warning';
  if (event.includes('REDEMPTION') || event.includes('SELL') || event.includes('INCOMING')) return 'secondary';
  if (event.includes('SUBSCRI') || event.includes('BUY') || event.includes('OUTGOING') || event.includes('ORDER')) {
    return 'primary';
  }

  return 'info';
};

export const zohoStatusView = (status?: JournalZohoStatus): { label: string; color: ChipColor } => {
  switch (status) {
    case 'SYNCED':
      return { label: 'In Zoho', color: 'success' };
    case 'PENDING':
      return { label: 'Zoho pending', color: 'warning' };
    case 'PROCESSING':
      return { label: 'Zoho sending', color: 'info' };
    case 'FAILED':
      return { label: 'Zoho failed', color: 'error' };
    default:
      return { label: 'Not in Zoho', color: 'default' };
  }
};

/** `AssetsAccounts:BrokerClient` → `BrokerClient`. */
export const shortAccount = (account: string) => account.split(':').pop() || account;

const parseUrlDate = (value: string | null) => {
  if (!value) return null;

  const date = parse(value, URL_DATE_FORMAT, new Date());

  return isValid(date) ? date : null;
};

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/** The date range a preset stands for; `custom` uses the picked dates. Both ends inclusive. */
export const resolveDateRange = (preset: JournalDatePreset, from: Date | null, to: Date | null) => {
  const now = new Date();

  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case '7d':
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    case '30d':
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    case 'month':
      return { from: startOfMonth(now), to: endOfDay(now) };
    case 'custom':
      return { from: from ? startOfDay(from) : null, to: to ? endOfDay(to) : null };
    default:
      return { from: null, to: null };
  }
};

export const readUrlState = (searchParams: URLSearchParams): JournalUrlState => {
  const preset = searchParams.get(URL_KEYS.preset) as JournalDatePreset | null;
  const perPage = parsePositiveInt(searchParams.get(URL_KEYS.perPage), DEFAULT_PER_PAGE);

  return {
    form: {
      userId: searchParams.get(URL_KEYS.userId) ?? '',
      companyId: searchParams.get(URL_KEYS.companyId) ?? '',
      employeeId: searchParams.get(URL_KEYS.employeeId) ?? '',
      event: searchParams.get(URL_KEYS.event) ?? '',
      reference: searchParams.get(URL_KEYS.reference) ?? '',
      preset: DATE_PRESETS.some((option) => option.value === preset) ? (preset as JournalDatePreset) : DEFAULT_PRESET,
      from: parseUrlDate(searchParams.get(URL_KEYS.from)),
      to: parseUrlDate(searchParams.get(URL_KEYS.to)),
    },
    page: parsePositiveInt(searchParams.get(URL_KEYS.page), 1),
    perPage: PER_PAGE_OPTIONS.includes(perPage) ? perPage : DEFAULT_PER_PAGE,
    sortOrder: searchParams.get(URL_KEYS.sort) === 'asc' ? 'asc' : 'desc',
    book: searchParams.get(URL_KEYS.book) === 'units' ? 'units' : 'cash',
  };
};

/** URL state → query string. Empty values are left out so the link stays short. */
export const toUrlParams = (state: JournalUrlState, segment: JournalSegment): Record<string, string> => {
  const { form } = state;
  const entries: [string, string][] = [
    [URL_KEYS.userId, form.userId.trim()],
    [URL_KEYS.companyId, segment === 'B2B' ? form.companyId.trim() : ''],
    [URL_KEYS.employeeId, segment === 'B2B' ? form.employeeId.trim() : ''],
    [URL_KEYS.event, form.event],
    [URL_KEYS.reference, form.reference.trim()],
    [URL_KEYS.preset, form.preset === DEFAULT_PRESET ? '' : form.preset],
    [URL_KEYS.from, form.preset === 'custom' && form.from ? format(form.from, URL_DATE_FORMAT) : ''],
    [URL_KEYS.to, form.preset === 'custom' && form.to ? format(form.to, URL_DATE_FORMAT) : ''],
    [URL_KEYS.page, state.page > 1 ? String(state.page) : ''],
    [URL_KEYS.perPage, state.perPage !== DEFAULT_PER_PAGE ? String(state.perPage) : ''],
    [URL_KEYS.sort, state.sortOrder === 'asc' ? 'asc' : ''],
    [URL_KEYS.book, state.book === 'units' ? 'units' : ''],
  ];

  return Object.fromEntries(entries.filter(([, value]) => value !== ''));
};

const toId = (value: string) => {
  const parsed = Number(value.trim());

  return value.trim() && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

/** URL state → API params. */
export const toRequestParams = (state: JournalUrlState, segment: JournalSegment): FetchLedgerJournalsParams => {
  const { form } = state;
  const range = resolveDateRange(form.preset, form.from, form.to);

  return {
    page: state.page,
    perPage: state.perPage,
    sortOrder: state.sortOrder,
    book: state.book,
    userId: toId(form.userId),
    companyId: segment === 'B2B' ? toId(form.companyId) : undefined,
    employeeId: segment === 'B2B' ? toId(form.employeeId) : undefined,
    event: form.event || undefined,
    reference: form.reference.trim() || undefined,
    from: range.from?.toISOString(),
    to: range.to?.toISOString(),
  };
};

/** One row per journal line; the first line of each journal is marked and highlighted. */
const toExportRows = (journals: LedgerJournal[]) =>
  journals.flatMap((journal) =>
    journal.lines.map((line, index) => [
      index === 0 ? JOURNAL_START_MARKER : '',
      format(new Date(journal.datetime), 'dd MMM yyyy'),
      format(new Date(journal.datetime), 'HH:mm:ss'),
      journal.event,
      journal.memo,
      journal.referenceId,
      journal.userId ?? '',
      journal.companyId ?? '',
      journal.employeeId ?? '',
      journal.zoho?.status ?? 'NONE',
      journal.zoho?.zohoJournalId ?? '',
      line.zohoAccount ?? '',
      line.account,
      line.debit,
      line.credit,
    ]),
  );

export const exportJournals = (journals: LedgerJournal[], fileName: string, bookType: 'xlsx' | 'csv') => {
  const worksheet = XLSX.utils.aoa_to_sheet([[...EXPORT_HEADERS], ...toExportRows(journals)]);

  worksheet['!cols'] = EXPORT_HEADERS.map(() => ({ wch: 20 }));

  if (bookType === 'xlsx') {
    const highlight = { fill: { fgColor: { rgb: 'FFE0E8F0' }, patternType: 'solid' } };
    let row = 1;

    journals.forEach((journal) => {
      EXPORT_HEADERS.forEach((_, column) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];

        if (cell) cell.s = highlight;
      });
      row += journal.lines.length;
    });
  }

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Journals');
  XLSX.writeFile(workbook, `${fileName}.${bookType}`, { bookType, compression: bookType === 'xlsx' });
};
